import { useCallback, useEffect, useState } from 'react';
import { isNativePlatform } from '../lib/platform.js';
import { applyOtaUpdate, checkAndDownloadOta, type ReadyOtaUpdate } from '../lib/ota-updates.js';

interface UseOtaUpdate {
  /** A downloaded bundle waiting to be applied, or null. */
  ready: ReadyOtaUpdate | null;
  /** True while the WebView is reloading into the new bundle. */
  applying: boolean;
  /** Apply the ready bundle (reloads the app). */
  apply: () => void;
}

/**
 * OTA update check (M5), native-only. On mount it checks our backend and, if a
 * newer bundle exists, downloads it in the background. When one is ready it
 * exposes `ready` so the shell can prompt "Restart to apply". No-op on web.
 */
export function useOtaUpdate(): UseOtaUpdate {
  const [ready, setReady] = useState<ReadyOtaUpdate | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!isNativePlatform()) return;
    let cancelled = false;
    void checkAndDownloadOta().then((r) => {
      if (!cancelled && r) setReady(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const apply = useCallback(() => {
    if (!ready) return;
    setApplying(true);
    // set() reloads the WebView into the new bundle; this view is torn down.
    void applyOtaUpdate(ready.id).catch(() => setApplying(false));
  }, [ready]);

  return { ready, applying, apply };
}
